import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { resetPassword } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword({ token, new_password: newPassword });
      toast.success('Password updated. Please sign in.');
      navigate('/');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not reset password');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="max-w-md text-center space-y-2">
          <h1 className="font-display text-2xl">Invalid reset link</h1>
          <p className="text-sm text-neutral-500">This link is missing or has expired. Request a new one from the sign-in page.</p>
          <Button onClick={() => navigate('/')} className="mt-4 rounded-full" data-testid="reset-back-home">Back to Transformerus</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="w-full max-w-sm space-y-5" data-testid="reset-password-card">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-neutral-900 text-white flex items-center justify-center">
            <KeyRound className="w-5 h-5" />
          </div>
          <h1 className="font-display text-2xl mt-3">Choose a new password</h1>
          <p className="text-sm text-neutral-500 mt-1">Use at least 8 characters with a mix of letters and numbers.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-2.5">
          <Input
            data-testid="reset-new-password"
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
            className="h-11 rounded-xl"
          />
          <Input
            data-testid="reset-confirm-password"
            type="password"
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className="h-11 rounded-xl"
          />
          <Button
            type="submit"
            data-testid="reset-submit"
            disabled={submitting}
            className="w-full h-11 rounded-xl bg-neutral-900 text-white font-medium"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update password'}
          </Button>
        </form>
      </div>
    </div>
  );
}
